import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchLOPage, ApiError, type ConsumerLOPageResponse } from "../api";
import BranchingPlayer, { BranchingDots, type BranchingPlayerHandle } from "../components/BranchingPlayer";
import PlayerOverlay from "../components/PlayerOverlay";
import EndOfVideoCard from "../components/EndOfVideoCard";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import Skeleton from "../components/Skeleton";
import VideoCallModal from "../components/VideoCallModal";
import MessageModal from "../components/MessageModal";
import ChatWidget from "../components/ChatWidget";
import { Calendar, Video, Phone } from "../components/icons";
import { recordEvent } from "../lib/analytics";

const SIDEBAR_KEY = "brv-sidebar-collapsed";
// While the page is open, refetch the LO payload every 8s to detect when the
// loan officer goes live in the LO Console (flips video_call_enabled). The
// "Video Call" button then activates without the consumer needing to refresh.
const LIVE_POLL_MS = 8000;

function setMeta(selector: string, attr: string, value: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    const [, k, v] = /\[([^=]+)="([^"]+)"\]/.exec(selector) || [];
    if (k && v) el.setAttribute(k, v);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function applyMeta(name: string, headshot: string | null, license: string | null, state?: string | null) {
  document.title = `${name} — Loan Officer`;
  const subtitle = [
    license ? `NMLS #${license}` : null,
    state ? `Licensed in ${state}` : null,
  ].filter(Boolean).join(" · ") || "Connect with your loan officer.";
  setMeta('meta[name="description"]', "content", subtitle);
  setMeta('meta[property="og:title"]', "content", name);
  setMeta('meta[property="og:description"]', "content", subtitle);
  if (headshot) {
    setMeta('meta[property="og:image"]', "content", headshot);
    setMeta('meta[name="twitter:image"]', "content", headshot);
  }
}

export default function LOPage() {
  const { webAppId, agentSlug } = useParams<{ webAppId: string; agentSlug?: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<ConsumerLOPageResponse | null>(null);
  const [error, setError] = useState<{ status: number; message: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [callOpen, setCallOpen] = useState(false);
  const [messageOpen, setMessageOpen] = useState(false);
  const [eovShown, setEovShown] = useState(false);
  // Duration of the base video, lifted up from BranchingPlayer so we can
  // render the branching-points strip BELOW the video (outside the
  // overflow:hidden .video-wrap) without coupling the strip to the videoRef.
  const [videoDuration, setVideoDuration] = useState<number>(0);
  // Imperative handle to the player so clicking a dot can seek the video.
  const playerRef = useRef<BranchingPlayerHandle | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(SIDEBAR_KEY) === "1";
  });
  const lastLoaded = useRef<string>("");

  useEffect(() => {
    localStorage.setItem(SIDEBAR_KEY, sidebarCollapsed ? "1" : "0");
  }, [sidebarCollapsed]);

  // Force the referral-partner sidebar to be exactly as tall as the video
  // player. Pure CSS grid stretch wasn't reliable because the agent card's
  // inner flex layout altered the sidebar's intrinsic measurement; setting
  // height directly via JS is the only bulletproof way.
  useEffect(() => {
    if (!data) return;
    const vw = document.querySelector<HTMLElement>(".video-wrap");
    const sb = document.querySelector<HTMLElement>(".sidebar");
    if (!vw || !sb) return;
    const apply = () => {
      const h = vw.getBoundingClientRect().height;
      if (h > 0) sb.style.height = `${Math.round(h)}px`;
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(vw);
    window.addEventListener("resize", apply);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", apply);
    };
  }, [data, sidebarCollapsed, agentSlug]);

  const load = useCallback(async (slug?: string) => {
    if (!webAppId) return;
    const key = `${webAppId}::${slug ?? ""}`;
    if (lastLoaded.current === key) return;
    lastLoaded.current = key;
    setLoading(true);
    setError(null);
    setEovShown(false);
    try {
      const resp = await fetchLOPage(webAppId, slug);
      setData(resp);
      applyMeta(resp.lo.name, resp.lo.headshot_url, resp.lo.license, resp.lo.state);
      // Record page-view event for analytics (fire-and-forget).
      recordEvent({
        lo_id: resp.lo.loan_officer_id,
        web_app_id: resp.lo.web_app_id,
        agent_slug: resp.selected?.agent_slug ?? null,
        event_type: "page_view",
      });
      // Per client direction: the LO's own URL stays the LO's own URL.
      // We do NOT auto-redirect to the first agent's slug — the LO's
      // page is about the LO only. The agent sidebar only appears when
      // the visitor explicitly lands on /lo/<id>/<agent_slug>.
      void slug; // intentionally not used for redirect
    } catch (e) {
      const apiE = e as ApiError;
      setError({ status: apiE.status ?? 0, message: apiE.message ?? "Unknown error" });
    } finally {
      setLoading(false);
    }
  }, [webAppId, navigate]);

  useEffect(() => {
    void load(agentSlug);
  }, [load, agentSlug]);

  // Lightweight live-status poll: refresh just the flag without touching loader/EOV state.
  useEffect(() => {
    if (!webAppId || error) return;
    const t = window.setInterval(async () => {
      if (document.hidden) return;
      try {
        const resp = await fetchLOPage(webAppId, agentSlug);
        setData((prev) => {
          // Only patch if a flag actually changed (avoid pointless re-renders).
          if (!prev) return resp;
          if (prev.flags.video_call_enabled === resp.flags.video_call_enabled) return prev;
          return { ...prev, flags: resp.flags };
        });
      } catch { /* ignore transient errors */ }
    }, LIVE_POLL_MS);
    return () => window.clearInterval(t);
  }, [webAppId, agentSlug, error]);

  // No agent-picker on the consumer page anymore (client direction:
  // each URL shows exactly one LO + at most one co-branding partner).
  // The redirect helper that used to live here was deleted with the picker.

  const handleEndOfVideoRedirect = useCallback((url: string) => {
    // If we're inside an iframe, navigate the *top* window so the parent page
    // moves to the LO's main page rather than swapping the embedded iframe.
    try {
      const target = (window.top && window.top !== window.self) ? window.top : window;
      target.location.assign(url);
    } catch {
      window.location.assign(url);
    }
  }, []);

  if (loading && !data) return <Skeleton />;

  if (error) {
    if (error.status === 404) {
      return (
        <div className="msg">
          <div className="msg__title">Page not found</div>
          <div className="msg__sub">No loan officer matches this URL. Double-check the link you were given.</div>
        </div>
      );
    }
    return (
      <div className="msg">
        <div className="msg__title">Something went wrong</div>
        <div className="msg__sub">{error.message}</div>
      </div>
    );
  }

  if (!data) return null;

  const { lo, agents, selected, preroll, branching, flags, call } = data;
  const callable = flags.video_call_enabled;
  // When the LO is offline, the call button diverts to a "Send a message"
  // form (Charles SoW item 2A). Both states use the same button slot.
  const callBtnTitle = callable
    ? `Start a live video call with ${lo.first_name || lo.name}`
    : `${lo.first_name || lo.name} isn't available right now. Leave a message and they'll follow up.`;

  return (
    <div className="page">
      <Topbar loState={lo.state ?? null} loLicense={lo.license} />

      {/* The referral-partner sidebar only renders when the visitor lands
          on an agent URL (/lo/<id>/<slug>) and we resolved that agent.
          The LO's own URL (/lo/<id>) is about the LO only — no sidebar. */}
      <main className={`layout ${!selected ? "layout--solo" : (sidebarCollapsed ? "layout--collapsed" : "")}`}>
        <section className="video-col">
          <div className="video-wrap">
            {preroll ? (
              <BranchingPlayer
                ref={playerRef}
                basePlaybackUrl={preroll.playbackUrl}
                branching={branching}
                onEnded={() => setEovShown(true)}
                poster={lo.headshot_url}
                onDuration={setVideoDuration}
                analytics={{
                  lo_id: lo.loan_officer_id,
                  web_app_id: lo.web_app_id,
                  agent_slug: selected?.agent_slug ?? null,
                  base_video_id: preroll.videoId,
                }}
              />
            ) : (
              <div className="video-placeholder">
                {lo.headshot_url && (
                  <img src={lo.headshot_url} alt="" className="video-placeholder__photo" />
                )}
                <div className="video-placeholder__title">A personal welcome is on the way</div>
                <div className="video-placeholder__sub">
                  {lo.first_name || "Your loan officer"} hasn't recorded an intro video yet.
                </div>
              </div>
            )}

            <PlayerOverlay
              name={lo.name}
              license={lo.license}
              phone={lo.phone}
              headshotUrl={lo.headshot_url}
              loState={lo.state ?? null}
            />

            {eovShown && (
              <EndOfVideoCard
                loName={lo.first_name || lo.name}
                redirectUrl={lo.end_of_video_url}
                onRedirect={handleEndOfVideoRedirect}
                onCancel={() => setEovShown(false)}
              />
            )}
          </div>

          {/* Branching-points timeline strip — rendered OUTSIDE the
              overflow:hidden .video-wrap so it never overlays the player
              chrome or the video content. Only shown when there is more
              than one branching point and the base video metadata has
              loaded so we know the duration. */}
          {branching && branching.points.length > 0 && videoDuration > 0 && (
            <BranchingDots
              points={branching.points}
              duration={videoDuration}
              onSeek={(t) => playerRef.current?.seekTo(t)}
            />
          )}
        </section>

        {selected && (
          <Sidebar
            agents={agents}
            selected={selected}
            collapsed={sidebarCollapsed}
            onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
          />
        )}

        {/* Action row + secondary message link live in their own grid row
            so the video column and the sidebar can share row 1 with equal
            heights, and the actions naturally stretch under the video. */}
        <div className="actions-section">
          <div className="actions">
            {lo.booking_url ? (
              <a
                className="btn btn--primary btn--lg"
                href={lo.booking_url}
                target="_blank"
                rel="noopener noreferrer"
                title="Opens the loan officer's booking page in a new tab"
              >
                <Calendar size={16} />
                Book a consultation
              </a>
            ) : (
              <button
                className="btn btn--primary btn--lg"
                disabled
                title="This loan officer hasn't set a booking link yet"
              >
                <Calendar size={16} />
                Book a consultation
              </button>
            )}

            {callable ? (
              <button
                className="btn btn--secondary btn--lg"
                onClick={() => setCallOpen(true)}
                title={callBtnTitle}
              >
                <Video size={16} />
                Video call
                <span className="badge badge--live">Live</span>
              </button>
            ) : (
              <button
                className="btn btn--secondary btn--lg"
                onClick={() => setMessageOpen(true)}
                title={callBtnTitle}
              >
                <Video size={16} />
                Send a message
                <span className="badge">Offline</span>
              </button>
            )}

            {lo.phone && (
              <a
                className="btn btn--ghost btn--lg"
                href={`tel:${lo.phone.replace(/[^\d+]/g, "")}`}
              >
                <Phone size={16} />
                {lo.phone}
              </a>
            )}
          </div>

          {/* When the LO is live, the offline message form is still available
              as a quieter secondary option so a visitor who prefers async
              contact can leave a note without needing a video call. */}
          {callable && (
            <button
              type="button"
              className="message-cta"
              onClick={() => setMessageOpen(true)}
              title={`Send ${lo.first_name || lo.name} a written message instead of calling`}
            >
              Or send {lo.first_name || lo.name} a message
            </button>
          )}
        </div>
      </main>

      <footer className="footer">
        <div className="footer__line">
          Equal Housing Lender. {lo.license && <>NMLS #{lo.license}. </>}
          Information provided is for educational purposes only and does not constitute a loan offer.
        </div>
      </footer>

      <VideoCallModal
        open={callOpen}
        onClose={() => setCallOpen(false)}
        loName={lo.name}
        loHeadshotUrl={lo.headshot_url}
        enabled={flags.video_call_enabled}
        callConfig={call}
        onBook={() => {
          if (lo.booking_url) window.open(lo.booking_url, "_blank", "noopener,noreferrer");
        }}
      />

      <MessageModal
        open={messageOpen}
        onClose={() => setMessageOpen(false)}
        loId={lo.loan_officer_id}
        loName={lo.name}
        webAppId={lo.web_app_id}
        agentSlug={selected?.agent_slug ?? null}
      />

      <ChatWidget />
    </div>
  );
}
